import { getHealthCheckStats } from '../../api/healthCheck';
import type { IDailyStat, IFilterState } from './types';
import { dateAdd, dateDiff } from './utils';
import { normalizeDaily } from './normalize';

export function enumerateDates(start: string, end: string): string[] {
  if (!start || !end || start > end) return [];
  const out: string[] = [];
  let d = start;
  const n = dateDiff(start, end);
  for (let i = 0; i <= n; i++) {
    out.push(d);
    if (d === end) break;
    d = dateAdd(d, 1);
  }
  return out;
}

/** Union of current selection and compare period (if enabled). */
export function requiredLoadRange(filter: IFilterState): { start: string; end: string } | null {
  if (!filter.start || !filter.end) return null;
  let start = filter.start;
  let end = filter.end;
  if (filter.compare && filter.cstart && filter.cend) {
    if (filter.cstart < start) start = filter.cstart;
    if (filter.cend > end) end = filter.cend;
  }
  return { start, end };
}

/** Contiguous ranges of dates in [start,end] that are not in cache. */
export function missingDateRanges(
  cachedDates: Set<string>,
  start: string,
  end: string
): { start: string; end: string }[] {
  const missing = enumerateDates(start, end).filter((d) => !cachedDates.has(d));
  if (missing.length === 0) return [];

  const ranges: { start: string; end: string }[] = [];
  let rangeStart = missing[0];
  let rangeEnd = missing[0];

  for (let i = 1; i < missing.length; i++) {
    if (dateAdd(rangeEnd, 1) === missing[i]) {
      rangeEnd = missing[i];
    } else {
      ranges.push({ start: rangeStart, end: rangeEnd });
      rangeStart = missing[i];
      rangeEnd = missing[i];
    }
  }
  ranges.push({ start: rangeStart, end: rangeEnd });
  return ranges;
}

export async function fetchDailyRange(start: string, end: string): Promise<IDailyStat[]> {
  const payload = await getHealthCheckStats({
    createdAt_min: start,
    createdAt_max: end,
  });
  const daily = normalizeDaily(payload, end);

  if (daily.length > 1) {
    return daily;
  }

  if (daily.length === 1 && start !== end) {
    const days = enumerateDates(start, end);
    const perDay = await Promise.all(
      days.map(async (dateStr) => {
        const p = await getHealthCheckStats({
          createdAt_min: dateStr,
          createdAt_max: dateStr,
        });
        const rows = normalizeDaily(p, dateStr);
        return rows[0] ?? null;
      })
    );
    return perDay.filter((d): d is IDailyStat => d != null);
  }

  return daily;
}

export function mergeDailyIntoCache(
  cache: Record<string, IDailyStat>,
  rows: IDailyStat[]
): Record<string, IDailyStat> {
  const next = { ...cache };
  rows.forEach((row) => {
    if (row.statDate) next[row.statDate] = row;
  });
  return next;
}

export function cacheToSortedArray(cache: Record<string, IDailyStat>): IDailyStat[] {
  return Object.values(cache).sort((a, b) => a.statDate.localeCompare(b.statDate));
}

export async function fetchMissingForFilter(
  cache: Record<string, IDailyStat>,
  filter: IFilterState
): Promise<Record<string, IDailyStat>> {
  const range = requiredLoadRange(filter);
  if (!range) return cache;

  const cachedDates = new Set(Object.keys(cache));
  const ranges = missingDateRanges(cachedDates, range.start, range.end);
  if (ranges.length === 0) return cache;

  let merged = cache;
  for (const r of ranges) {
    const rows = await fetchDailyRange(r.start, r.end);
    merged = mergeDailyIntoCache(merged, rows);
  }
  return merged;
}
